import { Bell, BookOpenCheck, Globe, LucideIcon, PlayCircle, TrendingUp } from "lucide-react";

// Flat-icon + badge-color styling for a notification's category. Shared by the
// All Notifications page and the header bell popup via `NotificationCategoryIcon`
// (src/components/reusable/NotificationCategoryIcon.tsx) — add new categories
// here, not in either consumer.
export interface CategoryIconStyle {
  Icon: LucideIcon;
  // Modifier suffix consumed by `.notification-category-icon--{modifier}` in
  // Notifications.scss, which owns the actual background/icon colors.
  modifier: string;
}

export const categoryIconStyles: { [key: string]: CategoryIconStyle } = {
  "Web Analytics": { Icon: Globe, modifier: "web" },
  "Track Sales Performance": { Icon: TrendingUp, modifier: "sales" },
  "Social Media Connectivity": { Icon: PlayCircle, modifier: "social" },
  Insights: { Icon: BookOpenCheck, modifier: "insights" },
};

export const defaultCategoryIconStyle: CategoryIconStyle = { Icon: Bell, modifier: "default" };

export interface InAppNotification {
  notification_id: number;
  content: string;
  notification_content?: string;
  category: string;
  activity?: string;
  alert_source?: string;
  is_read: boolean;
  created_at: string;
}

export interface GroupedNotifications {
  [key: string]: InAppNotification[];
}

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

// ---- Notification Settings v2 types ----

export interface NotificationOption {
  value: string;
  label: string;
}

export interface NotificationOptionsResponse {
  status: boolean;
  // Per-metric period options (localized {value,label}). Keyed by system, then
  // by metric key — drives each metric's Period dropdown.
  metric_periods: Partial<
    Record<NotificationSystem, Record<string, NotificationOption[]>>
  >;
  // Per-metric comparison-window options (localized). Keyed by system, then by
  // metric key — drives the Comparison dropdown (e.g. sales revenue_growth → wow/mom).
  metric_comparison_periods: Partial<
    Record<NotificationSystem, Record<string, NotificationOption[]>>
  >;
  operators: NotificationOption[];
  frequencies: NotificationOption[];
  threshold_types: NotificationOption[];
}

export interface PaidMediaCampaign {
  platform: string;
  platform_label: string;
  campaign_id: string;
  campaign_name: string;
}

export interface PaidMediaRule {
  rule_id: number;
  user_id: number;
  metric: string;
  metric_label: string;
  platforms: string[];
  operator: string | null;
  threshold_value: number | null;
  threshold_value_upper: number | null;
  period: string;
  email: boolean;
  push: boolean;
  in_app: boolean;
  is_enabled: boolean;
  campaigns: PaidMediaCampaign[];
  last_evaluated_at: string | null;
  last_value: number | null;
}

export interface SocialTarget {
  platform: string;
  platform_label: string;
  page_id: string;
  page_name?: string;
}

export interface SocialRule {
  rule_id: number;
  user_id: number;
  rule_type: string;
  rule_type_label: string;
  operator: string | null;
  threshold_type: string | null;
  threshold_value: number | null;
  threshold_value_upper: number | null;
  period: string;
  email: boolean;
  push: boolean;
  in_app: boolean;
  is_enabled: boolean;
  platforms: string[];
  targets: SocialTarget[];
  last_evaluated_at: string | null;
  last_value: number | null;
}

export interface WebAnalyticsRule {
  rule_id: number;
  user_id: number;
  metric: string;
  metric_label: string;
  unit: string;
  operator: string | null;
  threshold_value: number | null;
  threshold_value_upper: number | null;
  period: string;
  cities: string[];
  email: boolean;
  push: boolean;
  in_app: boolean;
  is_enabled: boolean;
  last_evaluated_at: string | null;
  last_value: number | null;
}

export interface SalesProduct {
  product_id: string;
  product_name: string;
}

export interface SalesRule {
  rule_id: number;
  user_id: number;
  metric: string;
  metric_label: string;
  unit: string;
  operator: string | null;
  threshold_value: number | null;
  threshold_value_upper: number | null;
  period: string | null;
  comparison_period: string | null;
  cities: string[];
  products: SalesProduct[];
  email: boolean;
  push: boolean;
  in_app: boolean;
  is_enabled: boolean;
  last_evaluated_at: string | null;
  last_value: number | null;
}

export type NotificationSystem =
  | "sales"
  | "paid_media"
  | "social"
  | "web_analytics";
export type NotificationKind =
  | "threshold"
  | "summary"
  | "city"
  | "pages"
  | "growth";

export interface NotificationSettingsItem {
  system: NotificationSystem;
  key: string;
  label: string;
  kind: NotificationKind;
  unit: string;
  allowed_periods: string[] | null;
  allowed_comparison_periods: string[] | null;
  configured: boolean;
  rule: PaidMediaRule | SocialRule | WebAnalyticsRule | SalesRule | null;
}


export interface NotificationChannelMasters {
  email: boolean;
  push: boolean;
  in_app: boolean;
}

export interface NotificationSection {
  category: string;
  system: NotificationSystem;
  description: string;
  items: NotificationSettingsItem[];
}

export interface NotificationSettingsResponse {
  status: boolean;
  user_id: number;
  tenant_id: number;
  notification_refresh_frequency: string;
  channel_masters: NotificationChannelMasters;
  notification_sections: NotificationSection[];
}

export interface SocialPlatformPage {
  platform: string;
  platform_label: string;
  page_id: string;
  page_name: string;
}

export interface SocialPlatformData {
  key: string;
  label: string;
  available: boolean;
  account_name: string;
  pages: SocialPlatformPage[];
}

export const getDashboardNameFromCategory = (
  category: string,
  setSelectedMenuItem?: (val: string) => void
): string => {
  let dashboardName = "Geo-Spatial Analytics Dashboard";

  switch (category) {
    case "Insights":
      dashboardName = "Paid Media Dashboard";
      break;
    case "Track Sales Performance":
      dashboardName = "Sales & Product Analytics Dashboard";
      break;
    case "Social Media Connectivity":
      dashboardName = "Organic Media Dashboard";
      break;
    case "Web Analytics":
      dashboardName = "Web Analytics Dashboard";
      break;
  }

  if (setSelectedMenuItem) {
    setSelectedMenuItem(dashboardName);
  }

  return dashboardName;
};
